import { useEffect, useMemo, useState } from "react";
import {
  FiBarChart2,
  FiCalendar,
  FiDollarSign,
  FiDroplet,
  FiScissors,
} from "react-icons/fi";
import AdminCard from "../components/admin/AdminCard";
import StatsCard from "../components/admin/StatsCard";
import Loader from "../components/common/Loader";
import formatCurrency from "../utils/formatCurrency";
import { BOOKING_STATUS } from "../utils/constants";
import * as serviceService from "../services/serviceService";
import * as bookingService from "../services/bookingService";

const safeCallGetAllServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }

  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }

  throw new Error("Service list method was not found in serviceService.");
};

const safeCallGetServiceWithBookings = async (id) => {
  if (typeof serviceService.getServiceWithBookings === "function") {
    return serviceService.getServiceWithBookings(id);
  }

  if (typeof serviceService.getServiceWithBookingDetails === "function") {
    return serviceService.getServiceWithBookingDetails(id);
  }

  if (typeof serviceService.getServiceBookings === "function") {
    return serviceService.getServiceBookings(id);
  }

  throw new Error("Service-with-bookings method was not found in serviceService.");
};

const safeCallGetAllBookings = async () => {
  if (typeof bookingService.getAllBookings === "function") {
    return bookingService.getAllBookings();
  }

  if (typeof bookingService.getBookings === "function") {
    return bookingService.getBookings();
  }

  if (typeof bookingService.getAllAdminBookings === "function") {
    return bookingService.getAllAdminBookings();
  }

  throw new Error("Booking list method was not found in bookingService.");
};

const getBookingHennaType = (booking) => {
  return booking?.hennaType || booking?.service?.hennaType || "Unknown";
};

const isRevenueBooking = (booking) => {
  return (
    booking?.status === BOOKING_STATUS.CONFIRMED ||
    booking?.status === BOOKING_STATUS.COMPLETED
  );
};

const Analytics = () => {
  const [services, setServices] = useState([]);
  const [serviceAnalytics, setServiceAnalytics] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError("");

        const servicesResponse = await safeCallGetAllServices();
        const servicesData = servicesResponse?.data || servicesResponse;

        if (servicesData?.statusCode && servicesData.statusCode !== 200) {
          throw new Error(servicesData.message || "Failed to load services.");
        }

        const serviceList = Array.isArray(servicesData?.serviceList)
          ? servicesData.serviceList
          : [];
        setServices(serviceList);

        const bookingsResponse = await safeCallGetAllBookings();
        const bookingsData = bookingsResponse?.data || bookingsResponse;

        if (bookingsData?.statusCode && bookingsData.statusCode !== 200) {
          throw new Error(bookingsData.message || "Failed to load bookings.");
        }

        const bookingList = Array.isArray(bookingsData?.bookingList)
          ? bookingsData.bookingList
          : [];
        setBookings(bookingList);

        const analyticsResults = await Promise.all(
          serviceList.map(async (service) => {
            try {
              const response = await safeCallGetServiceWithBookings(service.id);
              const data = response?.data || response;

              if (data?.statusCode && data.statusCode !== 200) {
                return {
                  ...service,
                  bookings: [],
                  bookingCount: 0,
                  revenue: 0,
                  confirmedCount: 0,
                  completedCount: 0,
                  canceledCount: 0,
                };
              }

              const serviceDetails = data?.service || service;
              const serviceBookings = Array.isArray(serviceDetails?.bookings)
                ? serviceDetails.bookings
                : [];

              const confirmedCount = serviceBookings.filter(
                (booking) => booking?.status === BOOKING_STATUS.CONFIRMED
              ).length;

              const completedCount = serviceBookings.filter(
                (booking) => booking?.status === BOOKING_STATUS.COMPLETED
              ).length;

              const canceledCount = serviceBookings.filter(
                (booking) => booking?.status === BOOKING_STATUS.CANCELED
              ).length;

              const revenue = serviceBookings.reduce((sum, booking) => {
                if (!isRevenueBooking(booking)) return sum;
                return sum + (Number(booking?.bookingPrice) || 0);
              }, 0);

              return {
                ...serviceDetails,
                bookings: serviceBookings,
                bookingCount: serviceBookings.length,
                revenue,
                confirmedCount,
                completedCount,
                canceledCount,
              };
            } catch {
              return {
                ...service,
                bookings: [],
                bookingCount: 0,
                revenue: 0,
                confirmedCount: 0,
                completedCount: 0,
                canceledCount: 0,
              };
            }
          })
        );

        setServiceAnalytics(analyticsResults);
      } catch (err) {
        setError(err?.message || "We could not load analytics.");
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  const topServices = useMemo(() => {
    return [...serviceAnalytics]
      .sort((a, b) => {
        if ((b.bookingCount || 0) !== (a.bookingCount || 0)) {
          return (b.bookingCount || 0) - (a.bookingCount || 0);
        }
        return (b.revenue || 0) - (a.revenue || 0);
      })
      .slice(0, 5);
  }, [serviceAnalytics]);

  const revenueEstimate = useMemo(() => {
    return bookings.reduce((sum, booking) => {
      if (!isRevenueBooking(booking)) {
        return sum;
      }

      return sum + (Number(booking?.bookingPrice) || 0);
    }, 0);
  }, [bookings]);

  const statusBreakdown = useMemo(() => {
    return {
      confirmed: bookings.filter((b) => b.status === BOOKING_STATUS.CONFIRMED).length,
      canceled: bookings.filter((b) => b.status === BOOKING_STATUS.CANCELED).length,
      completed: bookings.filter((b) => b.status === BOOKING_STATUS.COMPLETED).length,
    };
  }, [bookings]);

  const hennaTypeBreakdown = useMemo(() => {
    const counts = bookings.reduce((acc, booking) => {
      const hennaType = getBookingHennaType(booking);
      acc[hennaType] = (acc[hennaType] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([hennaType, count]) => ({ hennaType, count }))
      .sort((a, b) => b.count - a.count);
  }, [bookings]);

  const mostBookedHennaType = useMemo(() => {
    return hennaTypeBreakdown[0]?.hennaType || "—";
  }, [hennaTypeBreakdown]);

  const averageBookingValue = useMemo(() => {
    const revenueBookings = bookings.filter(isRevenueBooking);

    if (revenueBookings.length === 0) return 0;

    const total = revenueBookings.reduce((sum, booking) => {
      return sum + (Number(booking?.bookingPrice) || 0);
    }, 0);

    return total / revenueBookings.length;
  }, [bookings]);

  if (loading) {
    return <Loader text="Loading analytics overview..." />;
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : null}

      <div className="responsive-three-col">
        <StatsCard
          title="Services Tracked"
          value={services.length}
          subtitle="Service records included in reporting"
          icon={<FiScissors size={22} />}
        />

        <StatsCard
          title="Bookings Tracked"
          value={bookings.length}
          subtitle="All bookings currently included in analytics"
          icon={<FiCalendar size={22} />}
        />

        <StatsCard
          title="Estimated Revenue"
          value={formatCurrency(revenueEstimate)}
          subtitle="From confirmed and completed bookings"
          icon={<FiDollarSign size={22} />}
        />
      </div>

      <div className="responsive-three-col">
        <StatsCard
          title="Top Henna Type"
          value={mostBookedHennaType}
          subtitle="Most selected across bookings"
          icon={<FiDroplet size={22} />}
        />

        <StatsCard
          title="Average Booking Value"
          value={formatCurrency(averageBookingValue)}
          subtitle="Average value of confirmed and completed bookings"
          icon={<FiDollarSign size={22} />}
        />

        <StatsCard
          title="Completed"
          value={statusBreakdown.completed}
          subtitle="Appointments successfully finished"
          icon={<FiBarChart2 size={22} />}
        />
      </div>

      <div className="responsive-three-col">
        <StatsCard
          title="Confirmed"
          value={statusBreakdown.confirmed}
          subtitle="Appointments currently confirmed"
          icon={<FiBarChart2 size={22} />}
        />

        <StatsCard
          title="Canceled"
          value={statusBreakdown.canceled}
          subtitle="Appointments canceled in the system"
          icon={<FiBarChart2 size={22} />}
        />

        <StatsCard
          title="Revenue Bookings"
          value={
            bookings.filter(
              (booking) =>
                booking?.status === BOOKING_STATUS.CONFIRMED ||
                booking?.status === BOOKING_STATUS.COMPLETED
            ).length
          }
          subtitle="Bookings counted toward revenue totals"
          icon={<FiBarChart2 size={22} />}
        />
      </div>

      <AdminCard
        title="Top Booked Services"
        subtitle="Services ranked by actual booking count and booking-level revenue."
      >
        <div className="max-h-[520px] overflow-y-auto pr-1">
          {topServices.length === 0 ? (
            <div className="empty-state">
              <p className="text-base font-medium text-[#3b2f2f]">
                No service analytics are available yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {topServices.map((service, index) => (
                <div
                  key={service.id || index}
                  className="rounded-2xl border border-goldlight bg-white p-5"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                        Rank #{index + 1}
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-henna">
                        {service?.serviceName || "Unnamed Service"}
                      </h3>
                      <p className="mt-1 text-sm text-muted-warm">
                        {service?.serviceType || "—"} • {service?.hennaType || "—"}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <div className="rounded-xl bg-soft-cream px-4 py-3 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Bookings
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.bookingCount || 0}
                        </p>
                      </div>

                      <div className="rounded-xl bg-soft-cream px-4 py-3 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Revenue
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {formatCurrency(service?.revenue || 0)}
                        </p>
                      </div>

                      <div className="rounded-xl bg-soft-cream px-4 py-3 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Confirmed
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.confirmedCount || 0}
                        </p>
                      </div>

                      <div className="rounded-xl bg-soft-cream px-4 py-3 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Completed
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.completedCount || 0}
                        </p>
                      </div>

                      <div className="rounded-xl bg-soft-cream px-4 py-3 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Base Price
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.servicePrice !== undefined &&
                          service?.servicePrice !== null
                            ? formatCurrency(service.servicePrice)
                            : "—"}
                        </p>
                      </div>

                      <div className="rounded-xl bg-soft-cream px-4 py-3 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Duration
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.serviceDurationMinutes
                            ? `${service.serviceDurationMinutes} min`
                            : "—"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {service?.serviceDescription ? (
                    <p className="mt-4 text-sm leading-6 text-muted-warm">
                      {service.serviceDescription}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminCard>

      <AdminCard
        title="Henna Type Breakdown"
        subtitle="See which henna options clients choose most often."
      >
        <div className="max-h-[420px] overflow-y-auto pr-1">
          {hennaTypeBreakdown.length === 0 ? (
            <div className="empty-state">
              <p className="text-base font-medium text-[#3b2f2f]">
                No henna type analytics are available yet.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {hennaTypeBreakdown.map((item) => (
                <div
                  key={item.hennaType}
                  className="rounded-2xl border border-goldlight bg-white p-5"
                >
                  <p className="text-sm font-semibold text-henna">{item.hennaType}</p>
                  <p className="mt-3 text-2xl font-semibold text-[#3b2f2f]">
                    {item.count}
                  </p>
                  <p className="mt-1 text-sm text-muted-warm">bookings</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminCard>

      <AdminCard
        title="Analytics Notes"
        subtitle="A quick explanation of what this reporting view reflects."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-[#3b2f2f]">
              Service Trend Visibility
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              Services are ranked by actual bookings, with revenue based on booking
              totals rather than base service price alone.
            </p>
          </div>

          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-[#3b2f2f]">
              Henna Type Trends
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              Henna selections are tracked directly from each booking, helping you
              spot real client preference trends more accurately.
            </p>
          </div>

          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-[#3b2f2f]">
              Revenue Direction
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              Revenue uses booking totals, which better reflects party size,
              add-ons, and Jaguar surcharges.
            </p>
          </div>
        </div>
      </AdminCard>
    </div>
  );
};

export default Analytics;